<template>
    <div class="cobalt-task-form">
        <div class="container p-0 mt-1 mb-4">
            <div class="row">
                <div class="col-lg-4">
                    <CoverUploader
                        :initialImage="taskData.imageFile"
                        :updateCover="updateCoverImage"
                        :removeCover="removeCoverImage"
                        :previewCover="previewCoverImage"
                        @file-selected="handleCoverSelected"
                    />
                </div>
                <div class="col-lg-8 d-flex flex-column">
                    <div class="np-group justify-content-between pb-2">
                        <div class="np-group">
                            <IndicatorDaysTo  
                                :dateStart="taskData.date_planned_start"
                                :dateEnd="taskData.date_planned_end"
                                classes="np-badge np-borderless np-font-xs"
                                background=""
                                v-if="!taskData.date_actual_end" 
                            />                    
                        </div>   
                        <div class="np-group">
                            <div class="np-badge np-inactive np-font-xs me-2">
                                {{ $t("UI.task.taskCreatedAt") }} {{ localeDateTime(taskData.date_create) }}
                            </div>                        
                            <div class="np-badge np-inactive np-font-xs">
                                {{ $t("UI.task.taskUpdatedAt") }} {{ localeDateTime(taskData.date_update) }}
                            </div>
                        </div>                       
                    </div>
                    <textarea class="form-control np-textarea np-font-m has-text-weight-bold flex-grow-1" id="taskTitle" autocomplete="off" 
                        v-model="taskData.title" :placeholder="$t('UI.task.name')"></textarea>                       
                </div>
            </div>
            <div class="row mt-4">
                <div class="col-lg-12">                       
                    <InlineEditor v-model="taskData.description" :placeholder="$t('UI.task.description')" />
                </div>
            </div>
        </div>

        <div class="mb-2">
            <ul class="nav nav-pills np-tabs np-nowrap" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                    <button class="np-tab np-nowrap w-100" :class="{ active: activeTab == 'params' }" @click="setActiveTab('params')" id="files-tab" data-bs-toggle="tab" data-bs-target="#params" type="button" role="tab" aria-controls="home" aria-selected="true">
                        <svg class="icon"><use href="@/assets/images/icons/file-doc.svg#icon"></use></svg>
                        {{ $t("UI.task.params") }}
                    </button>
                </li>
                
                <li class="nav-item" role="presentation">
                    <button class="np-tab np-nowrap w-100" :class="{ active: activeTab == 'checklist' }" @click="setActiveTab('checklist')" id="checklist-tab" data-bs-toggle="tab" data-bs-target="#checklist" type="button" role="tab" aria-controls="home" aria-selected="true">
                        <svg class="icon"><use href="@/assets/images/icons/checklist.svg#icon"></use></svg>
                        {{ $t("UI.task.checklist") }}
                        <span v-if="this.taskData.checklist && this.taskData.checklist.length > 0" class="np-badge-s np-inactive ms-1">{{ this.taskData.checklist.length }}</span>
                    </button>
                </li>

                <!--
                <li class="nav-item" role="presentation">
                    <button class="np-tab np-nowrap" :class="{ active: activeTab == 'budget' }" @click="setActiveTab('budget')" id="budget-tab" data-bs-toggle="tab" data-bs-target="#budget" type="button" role="tab" aria-controls="home" aria-selected="true">
                        <svg class="icon"><use href="@/assets/images/icons/wallet.svg#icon"></use></svg>
                        {{ $t("UI.task.budget") }}
                        <span v-if="this.taskData.files.length > 0" class="np-badge-s np-inactive ms-1">{{ this.taskData.files.length }}</span>
                    </button>
                </li>
                -->

                <li class="nav-item" role="presentation">
                    <button class="np-tab np-nowrap w-100" :class="{ active: activeTab == 'files' }" @click="setActiveTab('files')" id="files-tab" data-bs-toggle="tab" data-bs-target="#files" type="button" role="tab" aria-controls="home" aria-selected="true">
                        <svg class="icon"><use href="@/assets/images/icons/folder2.svg#icon"></use></svg>
                        {{ $t("UI.task.files") }}
                        <span v-if="this.taskData.files.length > 0" class="np-badge-s np-inactive ms-1">{{ this.taskData.files.length }}</span>
                    </button>
                </li>

                <li class="nav-item" role="presentation">
                    <button class="np-tab np-nowrap w-100" :class="{ active: activeTab == 'comments' }" @click="setActiveTab('comments')" id="comments-tab" data-bs-toggle="tab" data-bs-target="#comments" type="button" role="tab" aria-controls="home" aria-selected="true">
                        <svg class="icon"><use href="@/assets/images/icons/comment.svg#icon"></use></svg>
                        {{ $t("UI.task.comments") }}
                        <span v-if="this.taskData.comments?.length > 0" class="np-badge-s np-inactive ms-1">{{ this.taskData.comments.length }}</span>
                    </button>
                </li>                
            </ul>
        </div>      

        <div class="position-relative flex-grow-1">

                <div class="tab-content cobalt-task__tab-content" id="myTabContent">
                    <div class="tab-pane fade show" :class="{ active: activeTab === 'params' }" id="params" role="tabpanel" aria-labelledby="params-tab">
                        <div class="np-form-group justify-content-between p-4 mb-4">
                            <div class="d-flex flex-column pe-4">
                                <div class="np-font-m np-f-medium mb-2">{{ $t("UI.task.progress") }}</div>
                                <IndicatorProgress :progress="taskData.completion_percent" size="p-2" @click="callProgress" />
                            </div>
                            <div class="d-flex flex-column px-4">
                                <div class="np-font-m np-f-medium mb-2">{{ $t("UI.task.priority") }}</div>
                                    
                                    <Multiselect 
                                        class="np-priority-select"
                                        v-model="selectedPriority" 
                                        :placeholder="this.$t('UI.select')"
                                        selectLabel=""
                                        :options="priorityValues" 
                                        :searchable="false"
                                        track-by="value"
                                        label="label"
                                        selectedLabel=""
                                        deselectLabel=""
                                        :clearOnSelect="false"
                                        :allow-empty="false"
                                    >
                                        <template #singleLabel="props">
                                            <span class="np-badge np-borderless p-2" :class="props.option.class">
                                                {{ $t(props.option.label) }}
                                            </span>                                    
                                        </template>
                                        <template #option="props">
                                            <span class="np-badge np-borderless p-2" :class="props.option.class">
                                                {{ $t(props.option.label) }}
                                            </span>
                                        </template>
                                    </Multiselect> 
                                    <!--
                                    <vSelect
                                        class="align-items-center flex-grow-1"
                                        v-model="selectedPriority"
                                        :clearable="false"
                                        :options="priorityValues"
                                        :searchable="false"
                                        :filterable="false"
                                    >                                    
                                        <template #option="{ value, bg, color, label }">
                                            <p class="ms-3">{{ $t(label) }}</p>
                                        </template> 
                                    </vSelect>   -->                                 
                            </div>
                            <div class="d-flex flex-column px-4">
                                <div class="np-font-m np-f-medium mb-2">{{ $t("UI.task.date") }}</div>

                                <flat-pickr 
                                    v-model="datePlannedStart"                               
                                    :placeholder="this.$t('UI.selectDate')"
                                    :config="{
                                        enableTime: true,
                                        dateFormat: 'U',
                                        altInput: true,
                                        altFormat: 'd.m.Y H:i',
                                        time_24hr: true,
                                        static: false,
                                        onClose: (selectedDates) => {
                                            if (selectedDates.length) {
                                                this.$nextTick(() => {
                                                    this.handleDateChange(selectedDates[0])
                                                })
                                            }
                                        }                                    
                                    }"
                                >
                                </flat-pickr>
                            </div>
                            <div class="d-flex flex-column">
                                <div class="np-font-m np-f-medium mb-2">{{ $t("UI.task.deadline") }}</div>

                                <flat-pickr 
                                    v-model="datePlannedEnd" 
                                    :placeholder="this.$t('UI.selectDate')"
                                    :config="{
                                        enableTime: true,
                                        dateFormat: 'U',
                                        altInput: true,
                                        altFormat: 'd.m.Y H:i',
                                        time_24hr: true,
                                        minDate: disabledBeforeStartTime,
                                        onClose: (selectedDates) => {
                                            if (selectedDates.length) {
                                                this.$nextTick(() => {
                                                    this.handleDateChange(selectedDates[0])
                                                })
                                            }
                                        }                                       
                                    }"
                                >
                                </flat-pickr>
                            </div>                                                         
                        </div>

                        <div class="row p-4 mb-4">
                            <div class="col-4">
                                <div class="np-font-ml np-f-medium mb-2">{{ $t("UI.task.author") }}</div>
                                <div class="np-group pt-1">
                                    <CobaltProfile :firstName="taskData.author?.first_name" :lastName="taskData.author?.last_name" />  
                                    <svg class="ms-2"><use href="@/assets/images/icons/eye.svg#icon"></use></svg>
                                </div>
                            </div>

                            <div class="col-4">
                                <div class="np-font-ml np-f-medium mb-2">{{ $t("UI.task.inspector") }}</div>
                                <Multiselect 
                                    v-model="selectedControllers" 
                                    :placeholder="this.$t('UI.select')"
                                    :multiple="true"
                                    :options="projectUsers" 
                                    :class="['cobalt-task__multiselect']"
                                    track-by="id"                              
                                    selectLabel=""
                                    label="first_name"
                                    selectedLabel=""
                                    deselectLabel="&#x2715;"
                                    open-direction="bottom"
                                    @remove="removeController"
                                >
                                    <template #selection="{ values, search, isOpen }">
                                        <div class="multiselect__single cobalt-profile-list__wrapper" v-show="!isOpen">
                                            <div class="cobalt-profile-list__item" v-for="item in values">
                                                <CobaltProfile 
                                                    :firstName="item.first_name" 
                                                    :lastName="item.last_name" 
                                                    :short="values.length > 1 ? true : false"
                                                />
                                            </div>
                                        </div>
                                    </template>                            
                                    <template #option="props">
                                        <span class="np-font-s">
                                            {{ props.option.first_name + ' ' + props.option.last_name }}
                                        </span>
                                    </template>
                                </Multiselect>                                  
                            </div>  
                            
                            <div class="col-4">
                                <div class="np-font-ml np-f-medium mb-2">{{ $t("UI.task.performers") }}</div>
                                <Multiselect 
                                    v-model="selectedPerformers" 
                                    :placeholder="this.$t('UI.select')"
                                    :multiple="true"
                                    :options="projectUsers" 
                                    :class="['cobalt-task__multiselect']"
                                    track-by="id"                              
                                    selectLabel=""
                                    label="first_name"
                                    selectedLabel=""
                                    deselectLabel="&#x2715;"
                                    open-direction="bottom"
                                    @remove="removePerformer"
                                >
                                    <template #selection="{ values, search, isOpen }">
                                        <div class="multiselect__single cobalt-profile-list__wrapper" v-show="!isOpen">
                                            <div class="cobalt-profile-list__item" v-for="item in values">
                                                <CobaltProfile 
                                                    :firstName="item.first_name" 
                                                    :lastName="item.last_name" 
                                                    :short="values.length > 1 ? true : false"
                                                    size="np-avatar-small"
                                                />
                                            </div>
                                        </div>
                                    </template>                            
                                    <template #option="props">
                                        <span class="np-font-s">
                                            {{ props.option.first_name + ' ' + props.option.last_name }}
                                        </span>
                                    </template>
                                </Multiselect>    
                            </div>                              
                        </div>
                                                
                        <div class="np-form-group p-4 mb-4">
                            <div class="np-group np-group-column w-100">
                                <div class="np-group np-font-ml np-f-medium mb-2 align-items-center">
                                    <svg class="np-mr-2"><use href="@/assets/images/icons/folder-tree.svg#icon"></use></svg>
                                    <div class="np-group np-font-ml np-f-medium flex-grow-1"> 
                                        {{ $t("UI.task.subTasks") }}
                                        <div class="np-badge-s np-inactive ms-1" v-if="taskData.subtask">{{ taskData.subtask.length }}</div>
                                    </div>
                                    <button type="button" class="np-btn np-outline np-borderless me-3" @click="isSubtasksVisible = !isSubtasksVisible">
                                        <svg class="icon" v-if="isSubtasksVisible"><use href="@/assets/images/icons/eye-hide.svg#icon"></use></svg>
                                        <svg class="icon" v-else><use href="@/assets/images/icons/eye.svg#icon"></use></svg>
                                        <span v-if="isSubtasksVisible">{{ $t("UI.hide") }}</span>
                                        <span v-else>{{ $t("UI.show") }}</span>
                                    </button>                                    
                                </div> 
                                <button class="np-btn np-btn-primary-lighter align-self-start mb-4" @click="this.$emit('showModSubtask', 0, taskData.id)" v-if="isSubtasksVisible">
                                    <svg class="icon"><use href="@/assets/images/icons/plus.svg#icon"></use></svg>    
                                    {{ $t("UI.task.addSubtask") }}
                                </button> 
                                <div class="mb-2"  v-if="!taskData.parent_id && isSubtasksVisible">
                                    <ul class="np-list">
                                        <li class="np-group align-items-start p-2 mb-3 np-borderless" v-for="subtask in taskData.subtask" :key="subtask.id">
                                            <div class="np-btn np-btn-link me-4 " :class="{'completed' : subtask.date_actual_end}" @click="complete(true, subtask)">
                                                <svg v-if="subtask.date_actual_end" ><use href="@/assets/images/icons/checked2.svg#icon"></use></svg>
                                                <svg v-else><use href="@/assets/images/icons/task-incomplete.svg#icon"></use></svg>
                                            </div>                                             
                                            <div class="np-group np-group-column flex-grow-1">
                                                <div class="np-font-m np-f-medium mb-2" :class="{'completed' : subtask.date_actual_end}" @dblclick="this.$emit('showModSubtask', subtask.id, taskData.id)">
                                                    {{ subtask.title }}
                                                </div>
                                                <div class="np-group">
                                                    <div class="np-group me-2" v-if="subtask.files?.length > 0">
                                                        <svg class="np-mr-1"><use href="@/assets/images/icons/folder2.svg#icon"></use></svg>
                                                        <div class="np-badge-s np-inactive ms-1">{{ (subtask.files?.length).toLocaleString('en-US', {minimumIntegerDigits: 2, useGrouping:false}) }}</div>
                                                    </div>
                                                    <div class="np-group" v-if="subtask.comments?.length > 0">
                                                        <svg class="np-mr-1"><use href="@/assets/images/icons/comment.svg#icon"></use></svg>
                                                        <div class="np-badge-s np-inactive ms-1">{{ (subtask.comments?.length).toLocaleString('en-US', {minimumIntegerDigits: 2, useGrouping:false}) }}</div>
                                                    </div>                
                                                </div>                                
                                            </div>
                                            <div class="np-group">
                                                <button type="button" class="np-btn np-danger np-outline me-3" v-if="!this.subTaskFormState" @click="removeSubtask(subtask.id)">
                                                    <svg><use href="@/assets/images/icons/trash.svg#icon"></use></svg> 
                                                </button>
                                                <button type="button" class="np-btn np-freeze np-outline me-3" @click="this.$emit('showModSubtask', subtask.id, taskData.id)">
                                                    <svg><use href="@/assets/images/icons/eye.svg#icon"></use></svg> 
                                                </button>                                                
                                            </div>
                                        </li> 
                                    </ul>
                                </div> 
                            </div>  
                        </div>
                               
                        <div class="np-form-group justify-content-between p-4 mb-4">
                            <div class="row mb-4 align-items-start flex-grow-1" v-if="taskData.id">
                                <div class="np-group np-font-ml np-f-medium flex-grow-1 mb-3">
                                    {{ $t("UI.task.tags") }}
                                </div>

                                <div class="np-group selected-tags np-flex-wrap mb-3">
                                    <span v-for="(tag, index) in this.selectedTags" :key="index" class="np-badge-s np-gray np-font-s mt-1 me-1">
                                        {{ tag }}
                                        <button @click="doRemoveTag(tag)" class="np-borderless np-transparent ms-2">
                                            ✕ 
                                        </button>
                                    </span>
                                </div>
                                
                                <span class="cobalt-task-form__plaintext">
                                    <multiselect 
                                        v-model="taskData.tags" 
                                        tag-placeholder="" 
                                        :placeholder="$t('UI.task.searchOrAddTag')"
                                        selectLabel=""
                                        deselectLabel="✖"
                                        :options="this.tags" 
                                        :showNoOptions="false"
                                        :showNoResults="false"
                                        :multiple="true" 
                                        :taggable="true" 
                                        :hide-selected="true"
                                        :loading="this.isTagsLoading"
                                        :block-keys="['Tab', '']"
                                        @search-change="this.doSearchTags"
                                        @select="this.doAddTag"
                                        @tag="this.doAddTag"
                                        ref="taskTagInput">

                                        <template #tag="{ option, remove }">
                                            
                                        </template>
                                        <template #clear="props">
                                            <div class="multiselect__clear" v-if="taskData.tags?.length"
                                                @mousedown.prevent.stop="clearAll(props.search)">{{ props.option }}</div>
                                        </template>    
                                        <template #noOptions>
                                            <span>{{ $t("UI.nothingFound") }}</span>
                                        </template>                                                          
                                        <template #noResult>
                                            <span>Oops! No elements found. Consider changing the search query.</span>
                                        </template>

                                    </multiselect>
                                </span>
                            </div> 
                        </div>
                    </div>

                    <div class="tab-pane fade h-100 show" :class="{ active: activeTab === 'checklist'}" id="checklist" role="tabpanel" aria-labelledby="checklist-tab">
                        <div class="np-group np-group-column pb-4 h-100">
                            <div class="mb-2">
                                <SimpleInputForm class="w-100" :placeholder="this.$t('UI.task.addChecklistItem')" @onSubmit="doCheckItem" />
                            </div>
                            <div class="np-group flex-grow-1 py-2">
                                <SimpleBar data-simplebar-auto-hide="true" style="width: 100%;max-height: 100%;">                       
                                    <div class="np-panel flex-row align-items-center p-4 mb-2" v-for="checkItem in taskData.checklist" :key="checkItem.id">
                                        <div class="np-btn np-btn-link me-4 " 
                                            :class="{'completed' : checkItem.is_done}" 
                                            @click="completeCheckItem(checkItem)"
                                            v-if="selectedCheckItem.id != checkItem.id"
                                        >
                                            <svg v-if="checkItem.is_done" ><use href="@/assets/images/icons/checked2.svg#icon"></use></svg>
                                            <svg v-else><use href="@/assets/images/icons/task-incomplete.svg#icon"></use></svg>
                                        </div>                                             
                                        <div class="np-group np-flex-1">
                                            <div class="np-font-m" :class="{'completed' : checkItem.is_done}" v-show="selectedCheckItem.id != checkItem.id">
                                                {{ checkItem.name }}
                                            </div>                  
                                            <SimpleInputForm 
                                                v-show="selectedCheckItem.id == checkItem.id"
                                                class="checkListEditor w-100"
                                                :multiline="false"
                                                :ignoreOutsideClick="true"
                                                :value="checkItem.name"
                                                :ref="(el) => setCheckItemRef(el, checkItem.id)" 
                                                @onCancel="cancelCheckItem"
                                                @onSubmit="doCheckItem"
                                            />             
                                        </div>
                                        <div class="np-group ps-2" v-if="selectedCheckItem.id != checkItem.id">
                                            <button type="button" class="np-btn np-outline me-2" v-if="!this.subTaskFormState" @click="updateCheckItem(checkItem)">
                                                <svg><use href="@/assets/images/icons/edit.svg#icon"></use></svg> 
                                            </button>                                                  
                                            <button type="button" class="np-btn np-danger np-outline" v-if="!this.subTaskFormState" @click="removeCheckItem(checkItem)">
                                                <svg><use href="@/assets/images/icons/trash.svg#icon"></use></svg> 
                                            </button>                                                
                                        </div>
                                    </div> 
                                </SimpleBar>
                            </div>
                        </div>
                    </div>

                    <!--
                    <div class="tab-pane fade show" :class="{ active: activeTab === 'budget'}" id="budget" role="tabpanel" aria-labelledby="budget-tab">
                        <div class="np-widgets-panel pb-4">
                            <div class="np-widget">
                                <div class="np-widget-top">
                                    <p class="np-font-l np-f-medium np-lh-l">{{ this.taskData.budget ? this.taskData.budget : '---' }}</p>
                                </div>
                                <div class="np-widget-bottom">
                                    <span class="np-lh-m">{{ $t("UI.dashboard.planned") }}</span>
                                </div>
                            </div>
                            
                            <div class="np-widget">
                                <div class="np-widget-top">
                                    <p class="np-font-l np-f-medium np-lh-l">0</p>
                                </div>                        
                                <div class="np-widget-bottom">
                                    <span class="np-lh-m">{{ $t("UI.dashboard.spent") }}</span>
                                </div>
                            </div>

                            <div class="np-widget">
                                <div class="np-widget-top">
                                    <p class="np-font-l np-f-medium np-lh-l">0</p>
                                </div>                        
                                <div class="np-widget-bottom">
                                    <span class="np-lh-m">{{ $t("UI.dashboard.left") }}</span>
                                </div>
                            </div>

                            <div class="np-widget">
                                <div class="np-widget-top">
                                    <p class="np-font-l np-f-medium np-lh-l">0</p>
                                </div>                        
                                <div class="np-widget-bottom">
                                    <span class="np-lh-m">{{ $t("UI.dashboard.overspending") }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    -->

                    <div class="tab-pane fade show" :class="{ active: activeTab === 'files' }" id="files" role="tabpanel" aria-labelledby="files-tab">
                        <div class="flex flex-wrap cobalt-uploader">
                            <uploader
                                ref="uploaderRef"
                                :auto="false"
                                @file-added="onFilesAdded"
                                @file-error="onFilesError"
                            >
                                <uploader-drop>
                                    <span>{{ $t("UI.task.dropOrSelectFiles") }}</span>
                                    <uploader-btn>{{ $t("UI.task.selectFiles") }}</uploader-btn>
                                </uploader-drop>
                                <uploader-list></uploader-list>                                    
                            </uploader>
                            
                            <div class="uploader-list">
                                <div class="uploader-file" v-for="file in this.taskData.files" :key="file.id">
                                    <div class="uploader-file-name" @click="this.callViewer(file.id, this.taskData.files)"><i class="uploader-file-icon" icon="unknown"></i>{{ file.name }}</div>
                                    <div class="uploader-file-size">
                                        {{ formatBytes(file.fileSize) }}
                                    </div>
                                    <div class="uploader-file-actions">
                                        <a class="btn btn-base me-1" target="_blank" :download="file.filePath" :href="Config.SERVER + 'web/' + file.filePath">
                                            <img src="@/assets/images/dashboard/base-download-dark.svg">
                                        </a>
                                        <div class="devider">|</div>
                                        <button class="btn btn-base btn-warning me-1" @click="onFileDelete(file.id)">
                                            <img src="@/assets/images/dashboard/task-remove.svg">
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="tab-pane fade show h-100" :class="{ active: activeTab === 'comments' }" id="comments" role="tabpanel" aria-labelledby="comments-tab">
                        <div class="np-group np-group-column pb-4 h-100">
                            <Comments 
                                :comments="this.taskData.comments"
                                :taskId="this.taskData.id"
                                :isSubtask="false"
                                :activeUserId="this.activeUser?.id"
                                @refresh="refresh">
                            </Comments>
                        </div>
                    </div>
                </div>                
        </div>                       
    </div>  
</template>

<script>
import CobaltProfile   from "@/components/dashboard/parts/CobaltProfile.vue"
import CobaltPerformer from "@/components/dashboard/parts/CobaltPerformer.vue"
import CobaltProfilesList from "@/components/dashboard/parts/CobaltProfilesList.vue"
import CoverUploader from "@/components/elements/CoverUploader.vue"
import IndicatorDaysTo  from "@/components/elements/IndicatorDaysTo.vue" 
import IndicatorProgress  from "@/components/elements/IndicatorProgress.vue" 
import InlineEditor from "@/components/elements/InlineEditor.vue" 

import DatePicker from 'vue-datepicker-next'
import 'vue-datepicker-next/index.css'

import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Russian } from "flatpickr/dist/l10n/ru.js"

import Multiselect from 'vue-multiselect'
import { ref } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
import { mapGetters, mapActions } from 'vuex'
import { nameColor, localeDate, localeTime, localeDateTime, formatBytes, priorityOptions, eventDate, priorityValues } from '@/utils/format'
import Config from '../../../config.js'
import { clearTaskDate, updateCheck } from "@/api/boardApi.js"
import Comments from "@/components/elements/Comments.vue"
import { confirm } from "@/utils/confirmation.js"
import { createToast } from 'mosha-vue-toastify'
import SimpleInputForm from "@/components/elements/SimpleInputForm.vue"
import SimpleBar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';

export default {
    components: {
        flatPickr,
        DatePicker,
        Multiselect,
        CobaltProfile,
        CobaltPerformer,
        CobaltProfilesList,
        CoverUploader,
        IndicatorDaysTo,
        IndicatorProgress,
        Comments,
        InlineEditor,
        SimpleInputForm,
        SimpleBar
    },    
    props: {
        subTaskFormState: {
            type: Boolean,
            default: false
        },        
        activeUser: {
            type: Object,
            required: true
        },
        taskData: {
            type: Object,
            required: true
        },
        projectUsers: {
            type: Array,
            required: true
        },
        colors: {
            type: Array,
            required: false
        },        
        projectInfo: {
            type: Object,
            required: true
        }  
    },    
    data() {
        return {
            Config,
            alwaysFirstTab: true,
            activeTab: 'params',
            files: [],
            isTagsLoading: false,
            isSubtasksVisible: true,
            isCheckItemProcessed: false,
            selectedCheckItem: {},
            tags: [],
            priorityValues,
            checkItemsRefs: {}
        }
    },
    computed: {
        datePlannedStart: {
            get() {
                return this.taskData.date_planned_start ? this.taskData.date_planned_start + "" : null
            },
            set(option) {
                this.taskData.date_planned_start = option ? parseInt(option) : null
            }
        },
        datePlannedEnd: {
            get() {
                return this.taskData.date_planned_end ? this.taskData.date_planned_end + "": null
            },
            set(option) {
                this.taskData.date_planned_end = option ? parseInt(option) : null
            }
        }, 
        disabledBeforeStartTime: {
            get() {
                return this.taskData.date_planned_start ? new Date(this.taskData.date_planned_start * 1000) : null
            }
        },             
        selectedPriority: {
            get() {
                return this.priorityValues.find(option => option.value === this.taskData.priority) || null
            },
            set(option) {
                this.taskData.priority = option ? option.value : null
            }
        },
        selectedPerformers: {
            get() {
                return this.taskData.performer
            },
            set(option) {
                if (Array.isArray(option) && option.length > 0) {
                    const updatedPerformers = [...option]
                    this.taskData.performer = updatedPerformers
                    this.updateTaskPerformer(true, false, option[option.length - 1])
                }
            }            
        },
        selectedControllers: {
            get() {
                return this.taskData.controller
            },
            set(option) {
                if (Array.isArray(option) && option.length > 0) {
                    const updatedControllers = [...option]
                    this.taskData.controller = updatedControllers
                    this.updateTaskController(true, false, option[option.length - 1])
                }
            }            
        },        
        selectedTags: {
            get() {
                return this.taskData.tags
            },
            set(option) {
                if (Array.isArray(option) && option.length > 0) {
                    const updatedTags = [...option]
                    this.taskData.tags = updatedTags
                }
            }   
        }        
    },
  
    methods: {
        ...mapActions('board', ['updatTsk', 'updatTskDate', 'clearTskDate', 'listPerformers', 'assignPerformer', 'detachPerformer', 'listControllers', 'assignController', 'detachController', 'uploadFiles', 'deleteFile', 'delSubTsk', 'addTaskComment', 'deleteTaskComment', 'searchTags', 'addTag', 'removeTag', 'uploadCover', 'removeCover', 'completeTask', 'completeSubTask', 'updateCheck', 'deleteCheck']),
        localeDate,
        localeTime,
        localeDateTime,
        formatBytes,
        nameColor,
        initialize(activeTab) {
            this.setActiveTab( activeTab )            
            this.replyToComment = null
            this.$refs.uploaderRef.fileList = []    
            this.selectedCheckItem = {}
        },        
        setCheckItemRef(el, id) {
        if (el) {
            this.checkItemsRefs[id] = el
        }
        },              
        async updateTask() {
            let updateResult = await this.updatTsk(this.taskData)
            
            await this.refresh()

            return updateResult
        },
        async updateTaskPerformer(operation, subtask, performer) {
            if (operation) {
                await this.assignPerformer({
                    task_id: this.taskData.id,
                    subtask: subtask,
                    performer_id: performer.id,
                })
            } else {
                await this.detachPerformer({
                    task_id: this.taskData.id,
                    subtask: subtask,
                    performer_id: performer.id,
                })                
            }
            await this.updateTask()
        },
        removePerformer(option, id) {
            const updatedPerformers = this.taskData.performer.filter(item => item.id !== option.id)
            this.taskData.performer = updatedPerformers
            this.updateTaskPerformer(false, false, option)
        }, 
        async updateTaskController(operation, subtask, controller) {
            if (operation) {
                await this.assignController({
                    task_id: this.taskData.id,
                    subtask: subtask,
                    controller_id: controller.id,
                })
            } else {
                await this.detachController({
                    task_id: this.taskData.id,
                    subtask: subtask,
                    controller_id: controller.id,
                })                
            }
            await this.updateTask()
        },
        removeController(option, id) {
            const updatedControllers = this.taskData.controller.filter(item => item.id !== option.id)
            this.taskData.controller = updatedControllers
            this.updateTaskController(false, false, option)
        },               
        async removeSubtask(id) {
            const confirmed = await confirm(this.$t("UI.task.removeSubtask"), this.$t("UI.confirmDelete"), this.$t("UI.ok"), this.$t("UI.cancel"))
            if (!confirmed) return

            const updatedSubtasks = this.taskData.subtask.filter(item => item.id !== id)
            this.taskData.subtask = updatedSubtasks
            let result = this.delSubTsk(id)

            await this.refresh()
        },
        handleDateChange(selectedDates) {
            if (!selectedDates) return
            this.updateTaskDate()
        },        
        async updateTaskDate() {
            const error = new Error();
            console.log(error.stack);
                        
            await this.updatTskDate(this.taskData)
            await this.updateTask()
        },
        async clearTaskDate() {
            await this.clearTskDate(this.taskData)
            await this.refresh()
        },   
        onFilesAdded(files) {
            this.files = this.files.concat(files);
            this.doUploadFiles();
        },  
        onFileDelete(id) {
            this.doDeleteFile(id)

            this.taskData.files = this.taskData.files.filter(function( obj ) {
                return obj.id !== id;
            });            

            this.refresh()
        },  
        async doDeleteFile(id) {
            try {
                const response = this.deleteFile(id)

                if (response) {
                    console.log('File remove successfully:', response);
                } else {
                    console.error('File remove failed:', response);
                }
            } catch (error) {
                console.error('Error removing files:', error);
            }
        },         
        async doUploadFiles() {
            if (this.files.length === 0) {
                console.log('No files to upload.');
                return;
            }

            const formData = new FormData();

            this.files.forEach((file, index) => {
                formData.append(`files[${index}]`, file.file);
            });

            formData.append('id', this.taskData.id);

            try {
                const response = this.uploadFiles(formData)

                if (response) {
                    console.log('Files uploaded successfully:', response);
                } else {
                    console.error('File upload failed:', response);
                }
            } catch (error) {
                console.error('Error uploading files:', error);
            }

            this.files = [];
        },
        onFilesError(file, error) {
            console.error('File upload failed:', file, error);
        }, 
        setActiveTab(tab) {
          this.activeTab = tab
        },     
        async doSearchTags(search) {   
            if (!search) return

            this.isLoading = true
            const searchData = {
                name: search,
                projectId: this.projectInfo.id
            }        
            let response = await this.searchTags(searchData)
            this.isTagsLoading = false
            this.tags = response ? response : []
        }, 
        async doSelectTag(tag) {
            this.tags.push(tag)
            this.taskData.tags = this.tags;            
        },
        async doAddTag(tag) {
            this.isLoading = true
            const tagData = {
                name: tag,
                task_id: this.taskData.id,
                isSubtask: false
            }

            let newTags = await this.addTag(tagData)
            if (newTags) 
                this.tags = newTags
            else    
                createToast({ description: this.$t("message.errorSavingTask") }, Config.TOAST_CONFIG_ERROR) 
            
            this.isTagsLoading = false
            this.taskData.tags = this.tags;

            this.$refs.taskTagInput.value.focus()
        },     
        async doRemoveTag(tag) {
            this.isLoading = true
            const tagData = {
                name: tag,
                task_id: this.taskData.id,
                isSubtask: false
            }
            this.tags = await this.removeTag(tagData)
            this.isTagsLoading = false

            this.taskData.tags = this.tags;
        },
        handleCoverSelected(file) {
            console.log("Выбранный файл:", file)
        },
        async updateCoverImage(file) {
            const formData = new FormData()
            formData.append("image", file)
            formData.append("id", this.taskData.id)

            let result = await this.uploadCover(formData)
            this.refresh()
        },
        async removeCoverImage() {
            let result = await this.removeCover(this.taskData.id)
            this.refresh()
        }, 
        previewCoverImage(_path) {
            if (_path) {
                this.callViewer(0, [{
                    id: 0,
                    name: _path,
                    filePath: _path
                }])
            }
        },
        async complete(isSubTask, taskData) {
            let isComplete = !!!taskData.date_actual_end
            if (isSubTask)
                await this.completeSubTask( { complete: isComplete, id: taskData.id } )
            else
                await this.completeTask( { complete: isComplete, id: taskData.id } )

            this.refresh()
            //this.$emit('close')
        },
        async refresh() {
            await this.$emit('refreshForm')
        },
        callViewer(id, files) {
            this.$emit('callViewer', id, files)
        },
        callProgress() {
            this.$emit('callProgress')
        },
        async doCheckItem(value) {
            const data = {
                id_task: this.taskData.id,
                name: value,
                is_done: false
            }

            if (this.selectedCheckItem.id) {
                data["id"] = this.selectedCheckItem.id
                data["name"] = value
                data["is_done"] = this.selectedCheckItem.is_done
            }
                
            await this.updateCheck(data)

            this.refresh()            
        },      
        async updateCheckItem(value) {
            this.selectedCheckItem = value
            this.$nextTick(() => {
                this.checkItemsRefs[value.id].focusIn()  
            })            
        },
        async completeCheckItem(item) {
            const data = {
                id: item.id,
                id_task: this.taskData.id,
                name: item.name,
                is_done: !item.is_done
            }

            await this.updateCheck(data)

            this.refresh()            
        },
        async removeCheckItem(item) {
            const confirmed = await confirm(this.$t("UI.task.removeChecklistItem"), this.$t("UI.confirmDelete"), this.$t("UI.ok"), this.$t("UI.cancel"))
            if (!confirmed) return

            const data = {
                id: item.id
            }
            const updatedChecklist = this.taskData.checklist.filter(it => it.id !== item.id)
            this.taskData.checklist = updatedChecklist

            await this.deleteCheck(data)            
            await this.refresh()            
        },
        cancelCheckItem() {
            this.selectedCheckItem = {}
        }
    }
}
</script>

<style>
.multiselect__tags-wrap {
  display: none;
}
.checkListEditor .np-input-group {
    padding: 0;
}
.checkListEditor .np-input-group.active {
    background-color: transparent;
    border: none;
    box-shadow: none;
}
.checkListEditor .np-input-group .np-text-input {
    padding-left: 0;
}
</style>